import React, { useEffect, useMemo, useState } from "react";
import {
  Bath,
  BedDouble,
  Building2,
  CalendarCheck,
  Check,
  CheckCircle2,
  Database,
  FolderOpen,
  Home,
  Landmark,
  Layers,
  Loader2,
  MapPin,
  PencilLine,
  Plus,
  Ruler,
  Search,
  Tag,
  Trash2,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { QuizQuestion } from "./QuizShell";
import { OptionCard } from "./OptionCard";
import {
  AiButton,
  digitsOnly,
  FeatureEditor,
  FieldLabel,
  makePhotoId,
  MAX_DESCRIPTION_LENGTH,
  MAX_FEATURE_NAME_LENGTH,
  MAX_FEATURES,
  MAX_GENERATION_ATTEMPTS,
  persistedUrl,
  PhotoPicker,
  QuizPhoto,
} from "./shared";
import type { QuizProgress } from "./types";

const MAX_PHOTOS = 4;
const MAX_LISTING_TAG_LENGTH = 20;

const LOADING_MESSAGES = [
  "Connecting to property records…",
  "Searching database…",
  "Retrieving property information…",
  "Extracting property details…",
  "Loading property features…",
  "Fetching images…",
  "Almost there…",
];

const FEATURE_SUGGESTIONS = [
  "Pool",
  "Garage",
  "Renovated kitchen",
  "Hardwood floors",
  "Fenced yard",
  "Fireplace",
  "Open floor plan",
  "Smart home",
];

type Screen =
  | "mode"
  | "source"
  | "saved"
  | "address"
  | "location"
  | "details"
  | "photos"
  | "describe"
  | "subject"
  | "review";

type Source = "saved" | "manual" | "search";

const buildFlow = (source: Source | null): Screen[] => {
  // "details" combines listing tag, property type, open house and key features on one screen;
  // the address screens also collect price / beds / baths / sqft.
  const tail: Screen[] = [
    "details",
    "photos",
    "describe",
    "subject",
    "review",
  ];
  if (source === "saved") return ["mode", "source", "saved"];
  if (source === "search") return ["mode", "source", "address", ...tail];
  return ["mode", "source", "location", ...tail];
};

interface Form {
  headline: string;
  address: string;
  price: string;
  bedrooms: string;
  bathrooms: string;
  sqft: string;
  description: string;
  subject: string;
  propertyType: string;
  status: string;
  is_open_house: boolean;
  listType: string;
}

const emptyForm: Form = {
  headline: "",
  address: "",
  price: "",
  bedrooms: "",
  bathrooms: "",
  sqft: "",
  description: "",
  subject: "",
  propertyType: "",
  status: "active",
  is_open_house: false,
  listType: "",
};

const mapPropertyType = (t?: string): string => {
  if (!t) return "Residential";
  const m: Record<string, string> = {
    "Single Family Residence": "Residential",
    SFR: "Residential",
    "Single Family": "Residential",
    "Multi-Family Residence": "Residential",
    MFR: "Residential",
    Condominium: "Residential",
    Condo: "Residential",
    Townhouse: "Residential",
    Commercial: "commercial",
    Land: "Residential",
  };
  return m[t] || "Residential";
};

const defaultDescription = (a: any): string => {
  const parts: string[] = [];
  if (a.propertyType) parts.push(a.propertyType);
  if (a.bedrooms && a.bathrooms) parts.push(`${a.bedrooms} bed, ${a.bathrooms} bath`);
  if (a.sqft) parts.push(`${parseInt(a.sqft).toLocaleString()} sq ft`);
  if (a.yearBuilt) parts.push(`built in ${a.yearBuilt}`);
  if (parts.length === 0) return "Property details imported. Please add additional information.";
  return parts.join(". ") + ".";
};

const money = (v: any) => {
  const n = Number(String(v ?? "").replace(/[^0-9.-]+/g, ""));
  return Number.isFinite(n) && n > 0 ? `$${n.toLocaleString()}` : "—";
};

export interface PropertyQuizProps {
  progress: QuizProgress;
  onTotal: (n: number) => void;
  properties: any[];
  loadingProperties: boolean;
  setMultiMode: (v: boolean) => void;
  onSelect: (property: any) => void;
  onAdd: (property: any) => void;
  onDelete: (id: string | number) => void;
  setCampaignSubject: (s: string) => void;
  getPropertyExpiry?: (
    createdAt?: string | null,
    planName?: string | null
  ) => { remainingDays: number; isExpired: boolean };
  formatExpiryMessage?: (days: number) => string;
  userPlan: string | null;
  onEnhancedImageUpdate: (p: {
    id: string;
    originalUrl: string;
    enhancedUrl: string;
    isHero: boolean;
  }) => void;
  getFullUrl: (u?: string | null) => string;
  /** Leave the property step (go to step 1). */
  onExit: () => void;
  /** Optional: open on a specific screen / source (defaults to the first question). */
  initialScreen?: string;
  initialSource?: "saved" | "manual" | "search";
}

export const PropertyQuiz: React.FC<PropertyQuizProps> = ({
  progress,
  onTotal,
  properties,
  loadingProperties,
  setMultiMode,
  onSelect,
  onAdd,
  onDelete,
  setCampaignSubject,
  getPropertyExpiry,
  formatExpiryMessage,
  userPlan,
  onEnhancedImageUpdate,
  getFullUrl,
  onExit,
  initialScreen,
  initialSource,
}) => {
  const { toast } = useToast();
  const [screen, setScreen] = useState<Screen>((initialScreen as Screen) || "mode");
  const [source, setSource] = useState<Source | null>(initialSource || null);
  const [form, setForm] = useState<Form>(emptyForm);
  const [photos, setPhotos] = useState<QuizPhoto[]>([]);
  const [features, setFeatures] = useState<string[]>([]);
  const [listTypes, setListTypes] = useState<{ id: number | string; list_name: string }[]>([]);
  const [customTag, setCustomTag] = useState("");
  const [savingTag, setSavingTag] = useState(false);
  const [saving, setSaving] = useState(false);

  // lookup
  const [lookupAddress, setLookupAddress] = useState("");
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [msgIdx, setMsgIdx] = useState(0);
  const [extra, setExtra] = useState<Record<string, string>>({});

  // AI counters
  const [descGen, setDescGen] = useState(0);
  const [subjGen, setSubjGen] = useState(0);
  const [aiBusy, setAiBusy] = useState<string | null>(null);

  // delete
  const [toDelete, setToDelete] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);

  const flow = useMemo(() => buildFlow(source), [source]);
  const idx = Math.max(0, flow.indexOf(screen));
  const position = progress.before + idx + 1;
  const total = progress.before + flow.length + progress.after;

  useEffect(() => {
    onTotal(flow.length);
  }, [flow.length, onTotal]);

  useEffect(() => {
    let active = true;
    axiosInstance
      .get(endpoints.propertylisttype)
      .then((r) => {
        if (active && Array.isArray(r.data)) setListTypes(r.data);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!searching) return;
    const t = setInterval(() => setMsgIdx((i) => (i + 1) % LOADING_MESSAGES.length), 1800);
    return () => clearInterval(t);
  }, [searching]);

  const set = <K extends keyof Form>(k: K, v: Form[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  // When the user taps "Edit" on the review screen we return there after the change.
  const [fromReview, setFromReview] = useState(false);
  const goNext = () => {
    if (fromReview) {
      setFromReview(false);
      setScreen("review");
      return;
    }
    const next = flow[idx + 1];
    if (next) setScreen(next);
  };
  const goBack = () => {
    if (fromReview) {
      setFromReview(false);
      setScreen("review");
      return;
    }
    if (idx === 0) onExit();
    else setScreen(flow[idx - 1]);
  };
  const editFromReview = (to: Screen) => {
    setFromReview(true);
    setScreen(to);
  };

  const resolveListTypeId = (name?: string | null) => {
    const n = String(name || "").trim().toLowerCase();
    if (!n) return undefined;
    const m = listTypes.find(
      (l) => String(l.list_name).toLowerCase() === n || String(l.id) === n
    );
    return m ? String(m.id) : undefined;
  };

  /* ───────── actions ───────── */

  const runLookup = async () => {
    const addr = lookupAddress.trim();
    if (!addr) {
      toast({ title: "Error", description: "Please enter the property address", variant: "destructive" });
      return;
    }
    if (searching) return;
    setSearching(true);
    setSearched(false);
    setMsgIdx(0);
    try {
      const res = await axiosInstance.post(endpoints.address_lookup, { address: addr });
      if (!res.data?.success) throw new Error(res.data?.error || "Property not found");
      const a = res.data.data || {};
      const photoList: string[] = res.data.photos || [];
      setForm((f) => ({
        ...f,
        headline: a.propertyTitle || a.address?.split(",")[0]?.trim() || "",
        address: a.address || addr,
        price: String(a.price || "").replace(/[^0-9]/g, ""),
        bedrooms: String(a.bedrooms || ""),
        bathrooms: String(a.bathrooms || ""),
        sqft: String(a.sqft || ""),
        propertyType: mapPropertyType(a.propertyType),
        status: a.mlsActive ? "active" : a.mlsStatus?.toLowerCase() || "active",
        subject: a.subject || `New Listing: ${a.propertyTitle || "Property"}`,
        description: a.description || defaultDescription(a),
        is_open_house: !!a.openHouse,
      }));
      setPhotos(
        photoList.slice(0, MAX_PHOTOS).map((url) => ({ id: makePhotoId(), url }))
      );
      setExtra({
        city: a.city || "",
        state: a.state || "",
        zip: a.zip || "",
        county: a.county || "",
        yearBuilt: a.yearBuilt || "",
        daysOnMarket: a.daysOnMarket || "",
      });
      setSearched(true);
      toast({
        title: "✅ Property details imported",
        description: photoList.length
          ? `Details and ${photoList.length} photos loaded from records.`
          : "No photos were available — you can upload your own.",
        duration: 4000,
      });
    } catch (err: any) {
      setPhotos([]);
      toast({
        title: "Search Failed",
        description: err?.response?.data?.error || err?.message || "Unable to retrieve property data.",
        variant: "destructive",
      });
    } finally {
      setSearching(false);
    }
  };

  const saveCustomTag = async () => {
    const name = customTag.trim().slice(0, MAX_LISTING_TAG_LENGTH);
    if (!name) {
      toast({ title: "Error", description: "Please enter a name for your custom tag", variant: "destructive" });
      return;
    }
    const existing = listTypes.find((l) => l.list_name.toLowerCase() === name.toLowerCase());
    if (existing) {
      set("listType", existing.list_name);
      setCustomTag("");
      return;
    }
    setSavingTag(true);
    try {
      const r = await axiosInstance.post(endpoints.propertylisttype, { list_name: name });
      const created = { id: r.data.id, list_name: r.data.list_name };
      setListTypes((p) => [...p, created]);
      set("listType", created.list_name);
      setCustomTag("");
      toast({ title: "Custom tag created", description: `"${created.list_name}" selected` });
    } catch (e: any) {
      toast({
        title: "Error",
        description:
          e?.response?.data?.list_name?.[0] || e?.response?.data?.error || "Failed to create custom tag",
        variant: "destructive",
      });
    } finally {
      setSavingTag(false);
    }
  };

  const aiPayload = () => ({
    address: form.address,
    price: form.price ? parseInt(form.price.replace(/[^0-9.-]+/g, "")) : 0,
    headline: form.headline,
    bedrooms: form.bedrooms ? parseInt(form.bedrooms) : 0,
    bathrooms: form.bathrooms ? parseInt(form.bathrooms) : 0,
    area: form.sqft ? parseInt(form.sqft) : 0,
  });

  const generateDescription = async () => {
    if (aiBusy || descGen >= MAX_GENERATION_ATTEMPTS) return;
    setAiBusy("gen-desc");
    try {
      const r = await axiosInstance.post(endpoints.generate_description, {
        ...aiPayload(),
        features: "",
      });
      const d = r.data?.description?.trim();
      if (d) {
        set("description", d.slice(0, MAX_DESCRIPTION_LENGTH));
        setDescGen((c) => c + 1);
        toast({ title: "Description generated", description: "AI-powered description added!" });
      }
    } catch (e: any) {
      toast({ title: "Generation failed", description: e?.message || "Unable to generate description", variant: "destructive" });
    } finally {
      setAiBusy(null);
    }
  };

  const generateSubject = async () => {
    if (aiBusy || subjGen >= MAX_GENERATION_ATTEMPTS) return;
    setAiBusy("gen-subj");
    try {
      const r = await axiosInstance.post(endpoints.generate_subject, aiPayload());
      const s = r.data?.subjects?.[0];
      if (s) {
        set("subject", s);
        setSubjGen((c) => c + 1);
        toast({ title: "Subject generated", description: "AI-powered subject line added!" });
      }
    } catch {
      toast({ title: "Generation failed", description: "Could not generate subject line", variant: "destructive" });
    } finally {
      setAiBusy(null);
    }
  };

  const polish = async (field: "description" | "subject", kind: "improve" | "grammar") => {
    const text = form[field]?.trim();
    if (!text || aiBusy) return;
    setAiBusy(`${kind}-${field}`);
    try {
      if (kind === "improve") {
        const r = await axiosInstance.post(endpoints.improve_text, { text });
        const out = r.data?.improved_text?.trim();
        if (out) set(field, field === "description" ? out.slice(0, MAX_DESCRIPTION_LENGTH) : out);
      } else {
        const r = await axiosInstance.post(endpoints.correct_grammar, { text });
        const out = r.data?.corrected_text?.trim();
        if (out) set(field, field === "description" ? out.slice(0, MAX_DESCRIPTION_LENGTH) : out);
      }
    } catch {
      toast({ title: "Failed", description: "Could not update the text", variant: "destructive" });
    } finally {
      setAiBusy(null);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await axiosInstance.delete(endpoints.delete_property(toDelete.id));
      onDelete(toDelete.id);
      toast({ title: "🗑️ Property deleted", description: "Property deleted successfully" });
      setToDelete(null);
    } catch (e: any) {
      toast({
        title: "Error",
        description: e?.response?.data?.detail || "Failed to delete property",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  const createProperty = async () => {
    const missing: string[] = [];
    if (!form.headline.trim()) missing.push("Title");
    if (!form.address.trim()) missing.push("Address");
    if (!form.price) missing.push("Price");
    if (!form.bedrooms) missing.push("Bedrooms");
    if (!form.bathrooms) missing.push("Bathrooms");
    if (!form.description.trim()) missing.push("Description");
    if (!form.propertyType) missing.push("Property Type");
    if (photos.length < 1) missing.push("Images");
    if (!form.subject.trim()) missing.push("Subject");
    if (features.length < 1) missing.push("Features");
    if (!resolveListTypeId(form.listType)) missing.push("Listing Tag");
    if (missing.length) {
      toast({
        title: "Missing Required Fields",
        description: `Please fill: ${missing.join(", ")}`,
        variant: "destructive",
      });
      return;
    }
    if (features.length > MAX_FEATURES) {
      toast({ title: "Feature Limit Reached", description: `Maximum ${MAX_FEATURES} features.`, variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      const fd = new FormData();
      const title = form.headline.trim() || form.address.split(",")[0].trim();
      fd.append("title", title);
      fd.append("address", form.address);
      const cleanPrice = parseInt(form.price.replace(/[^0-9.-]+/g, ""), 10);
      if (!isNaN(cleanPrice)) fd.append("price", String(cleanPrice));
      fd.append("bedrooms", form.bedrooms);
      fd.append("bathrooms", form.bathrooms);
      if (form.sqft) fd.append("area", form.sqft);
      fd.append("description", form.description);
      fd.append("property_type", form.propertyType || "family");
      fd.append("status", form.status || "active");
      fd.append("is_open_house", form.is_open_house ? "true" : "false");
      if (form.subject.trim()) fd.append("subject", form.subject.trim());
      const listTypeId = resolveListTypeId(form.listType);
      if (listTypeId) fd.append("propertyListType_id", String(listTypeId));
      photos.forEach((p, i) => {
        if (!p.file) return;
        if (i === 0) fd.append("image", p.file);
        else fd.append(`photo_${i}`, p.file);
      });

      const created = await axiosInstance.post(endpoints.properties, fd);
      const propertyId = created.data.id || created.data._id;

      for (const name of features.map((f) => f.slice(0, MAX_FEATURE_NAME_LENGTH))) {
        try {
          await axiosInstance.post(endpoints.keyfeatures, { feature_name: name, propetyId: propertyId });
        } catch {
          /* individual feature failures should not block the launch */
        }
      }

      const { data: server } = await axiosInstance.get(`${endpoints.properties}${propertyId}/`);
      const serverPhotos: string[] = Array.isArray(server.photos) ? server.photos : [];
      const listObj =
        server.propertyListType ||
        listTypes.find((l) => l.list_name === form.listType);
      const localUrls = photos.map((p) => persistedUrl(p.url)).filter(Boolean);
      const hero = localUrls[0] || getFullUrl(server.image) || "";
      const secondary = localUrls.length > 1 ? localUrls.slice(1) : serverPhotos.map(getFullUrl);

      const newProperty: any = {
        id: server.id || server._id,
        title: server.title || title,
        address: server.address || form.address,
        price: server.price || cleanPrice,
        bedrooms: server.bedrooms || parseInt(form.bedrooms),
        bathrooms: server.bathrooms || parseInt(form.bathrooms),
        area: server.area || parseInt(form.sqft) || 0,
        property_type: server.property_type || form.propertyType,
        status: server.status || form.status,
        image: hero,
        photos: secondary,
        is_open_house: server.is_open_house ?? false,
        subject: server.subject || form.subject.trim(),
        description: server.description || form.description,
        propertyListType: listObj
          ? { id: listObj.id, list_name: listObj.list_name || "" }
          : undefined,
      };

      onAdd(newProperty);
      toast({ title: "✅ Property added", description: "Property added successfully!", duration: 1500 });
      const finalSubject = (server.subject || "").trim() || form.subject.trim();
      if (finalSubject) setCampaignSubject(finalSubject);
      onSelect(newProperty);
    } catch (e: any) {
      toast({
        title: "Error",
        description:
          e?.response?.data?.detail ||
          e?.response?.data?.non_field_errors?.[0] ||
          e?.response?.data?.message ||
          "Failed to create property",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  /* ───────── screens ───────── */

  const q = { position, total } as const;
  const hasTag = !!resolveListTypeId(form.listType);
  const numbersValid = !!form.price && !!form.bedrooms && !!form.bathrooms;

  /** Price / beds / baths / sqft, shown under the address on the same screen. */
  const numbersCard = (
    <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <p className="mb-4 text-sm font-semibold text-slate-800">
        Key numbers <span className="font-normal text-slate-500">· price, bedrooms and bathrooms are required</span>
      </p>
      <div className="grid gap-5 sm:grid-cols-3">
        <div className="sm:col-span-3">
          <FieldLabel required>Price</FieldLabel>
          <div className="relative">
            <Landmark className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
            <Input
              inputMode="numeric"
              maxLength={12}
              value={form.price ? Number(form.price).toLocaleString() : ""}
              placeholder="450,000"
              onChange={(e) => set("price", digitsOnly(e.target.value))}
              className="h-12 pl-10 text-base"
            />
          </div>
        </div>
        <div>
          <FieldLabel required>Bedrooms</FieldLabel>
          <div className="relative">
            <BedDouble className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
            <Input inputMode="numeric" maxLength={3} value={form.bedrooms} placeholder="3" onChange={(e) => set("bedrooms", digitsOnly(e.target.value))} className="h-12 pl-10 text-base" />
          </div>
        </div>
        <div>
          <FieldLabel required>Bathrooms</FieldLabel>
          <div className="relative">
            <Bath className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
            <Input
              inputMode="decimal"
              maxLength={4}
              value={form.bathrooms}
              placeholder="2.5"
              onChange={(e) => /^\d*\.?\d?$/.test(e.target.value) && set("bathrooms", e.target.value)}
              className="h-12 pl-10 text-base"
            />
          </div>
        </div>
        <div>
          <FieldLabel hint="optional">Square feet</FieldLabel>
          <div className="relative">
            <Ruler className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
            <Input inputMode="numeric" maxLength={8} value={form.sqft} placeholder="1,800" onChange={(e) => set("sqft", digitsOnly(e.target.value))} className="h-12 pl-10 text-base" />
          </div>
        </div>
      </div>
    </div>
  );

  switch (screen) {
    case "mode":
      return (
        <QuizQuestion {...q} title="Is this launch for one property or multi property?" hint="Multi-property launches feature several listings in a single email." onBack={onExit} size="md">
          <div className="grid gap-4 sm:grid-cols-2">
            <OptionCard
              icon={Home}
              tone="blue"
              title="Just one property"
              description="A focused launch built around a single listing."
              onClick={() => {
                setMultiMode(false);
                goNext();
              }}
            />
            <OptionCard
              icon={Layers}
              tone="violet"
              title="Multi property"
              description="Pick multiple properties and templates in one launch."
              onClick={() => setMultiMode(true)}
            />
          </div>
        </QuizQuestion>
      );

    case "source":
      return (
        <QuizQuestion {...q} title="How would you like to add your property?" onBack={goBack} size="lg">
          <div className="grid gap-4 sm:grid-cols-3">
            <OptionCard
              icon={FolderOpen}
              tone="blue"
              title="Use a saved property"
              description="Pick from properties you've already added."
              badge={<Badge variant="secondary">{properties.length}</Badge>}
              onClick={() => {
                setSource("saved");
                setScreen("saved");
              }}
            />
            <OptionCard
              icon={Search}
              tone="emerald"
              title="Search by address"
              description="Auto-fill details and photos from public records."
              onClick={() => {
                setSource("search");
                setScreen("address");
              }}
            />
            <OptionCard
              icon={PencilLine}
              tone="amber"
              title="Enter it manually"
              description="Type in the details and upload your own photos."
              onClick={() => {
                setSource("manual");
                setScreen("location");
              }}
            />
          </div>
        </QuizQuestion>
      );

    case "saved": {
      const available = properties.filter((p) => {
        if (!getPropertyExpiry) return true;
        const created = p.created || p.created_at || p.createdAt || null;
        return !getPropertyExpiry(created, userPlan).isExpired;
      });
      const isPro = (userPlan || "").toLowerCase().includes("pro");
      return (
        <QuizQuestion {...q} title="Which property is this launch for?" hint="Select one of your saved properties." onBack={goBack} size="xl">
          <div className="mb-5 rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
            <span className="font-semibold">Property access window: </span>
            {isPro
              ? `Saved properties stay available for 60 days. You have 120 days with your ${userPlan} plan.`
              : "Saved properties stay available for 60 days. Upgrade to Pro or Pro+ for 120 days."}
          </div>

          {loadingProperties ? (
            <div className="space-y-3">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-28 w-full rounded-2xl" />
              ))}
            </div>
          ) : available.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-slate-300 bg-white p-10 text-center">
              <Home className="mx-auto mb-3 h-12 w-12 text-slate-300" />
              <p className="font-semibold text-slate-700">No saved properties yet</p>
              <p className="mt-1 text-sm text-slate-500">Add one by searching an address or entering it manually.</p>
              <div className="mt-5 flex flex-wrap justify-center gap-3">
                <Button
                  onClick={() => {
                    setSource("search");
                    setScreen("address");
                  }}
                >
                  <Search className="mr-2 h-4 w-4" /> Search by address
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSource("manual");
                    setScreen("location");
                  }}
                >
                  <PencilLine className="mr-2 h-4 w-4" /> Enter manually
                </Button>
              </div>
            </div>
          ) : (
            <div className="max-h-[520px] space-y-3 overflow-y-auto pr-1">
              {available.map((p) => {
                const img = p.image ? getFullUrl(p.image) : "";
                const created = p.created || p.created_at || p.createdAt || null;
                const expiry = getPropertyExpiry ? getPropertyExpiry(created, userPlan) : null;
                return (
                  <div
                    key={p.id}
                    className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-blue-300 hover:shadow-md sm:flex-row sm:items-center"
                  >
                    <div className="h-24 w-full shrink-0 overflow-hidden rounded-xl bg-slate-100 sm:h-20 sm:w-28">
                      {img ? (
                        <img src={img} alt="" className="h-full w-full object-cover" onError={(e) => ((e.target as HTMLImageElement).style.display = "none")} />
                      ) : (
                        <div className="flex h-full items-center justify-center">
                          <Home className="h-7 w-7 text-slate-300" />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-base font-semibold text-slate-900">{p.title || "Untitled property"}</p>
                      <p className="text-sm font-semibold text-red-500">{money(p.price)}</p>
                      <p className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-slate-500">
                        {p.bedrooms != null && <span>🛏 {p.bedrooms} bed</span>}
                        {p.bathrooms != null && <span>🚿 {p.bathrooms} bath</span>}
                        {p.area != null && <span>📐 {p.area} sqft</span>}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {p.property_type && <Badge variant="secondary">{p.property_type}</Badge>}
                        {p.status && <Badge className="bg-green-100 text-green-800 hover:bg-green-100">{p.status}</Badge>}
                        {expiry && !expiry.isExpired && formatExpiryMessage && (
                          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">⏰ {formatExpiryMessage(expiry.remainingDays)}</Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Button variant="outline" size="icon" title="Delete property" onClick={() => setToDelete(p)} className="h-10 w-10 border-red-200 text-red-500 hover:bg-red-50">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      <Button
                        onClick={() => {
                          onSelect(p);
                          if (p.subject) setCampaignSubject(p.subject);
                        }}
                        className="h-10 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 px-5 text-white"
                      >
                        Use this property
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <Dialog open={!!toDelete} onOpenChange={(o) => !o && !deleting && setToDelete(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete this property?</DialogTitle>
                <DialogDescription>
                  "{toDelete?.title}" will be removed permanently. This cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setToDelete(null)} disabled={deleting}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={confirmDelete} disabled={deleting}>
                  {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </QuizQuestion>
      );
    }

    case "address":
      return (
        <QuizQuestion
          {...q}
          title="What's the property address?"
          hint="We'll look it up and fill in the details and photos for you."
          onBack={goBack}
          onContinue={searched ? goNext : undefined}
          continueDisabled={!numbersValid}
          continueLabel="Looks good, continue"
          skipLabel="Enter details manually instead"
          onSkip={() => {
            setSource("manual");
            setScreen("location");
          }}
          size="md"
        >
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <FieldLabel required>Property address</FieldLabel>
            <div className="flex flex-col gap-2 sm:flex-row">
              <div className="flex-1">
                <AddressAutocomplete
                  id="quiz-lookup-address"
                  value={lookupAddress}
                  onChange={setLookupAddress}
                  placeholder="e.g. 2812 S Shine Ave, Orlando, FL"
                  showLabel={false}
                  disabled={searching}
                  className="h-12 text-base"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !searching && lookupAddress.trim()) runLookup();
                  }}
                />
              </div>
              <Button onClick={runLookup} disabled={searching} className="h-12 px-6">
                {searching ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
                Search
              </Button>
            </div>

            {searching && (
              <div className="mt-6 flex flex-col items-center gap-3 rounded-xl bg-blue-50 p-6">
                <span className="relative flex h-14 w-14 items-center justify-center">
                  <span className="absolute inset-0 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600" />
                  <Database className="h-6 w-6 text-blue-600" />
                </span>
                <p className="text-sm font-medium text-blue-900">{LOADING_MESSAGES[msgIdx]}</p>
              </div>
            )}

            {searched && !searching && (
              <div className="mt-6 rounded-xl border border-green-200 bg-green-50 p-4">
                <p className="mb-2 flex items-center gap-2 font-semibold text-green-800">
                  <CheckCircle2 className="h-5 w-5" /> Property found
                </p>
                <p className="text-sm font-medium text-slate-900">{form.address}</p>
                <p className="mt-1 text-sm text-slate-600">
                  {[form.bedrooms && `${form.bedrooms} bed`, form.bathrooms && `${form.bathrooms} bath`, form.sqft && `${form.sqft} sqft`, form.price && money(form.price)]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  {[extra.city, extra.state, extra.zip, extra.county && `${extra.county} County`, extra.yearBuilt && `Built ${extra.yearBuilt}`]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
                <p className="mt-2 text-xs text-slate-500">Check or change the numbers below, then continue.</p>
              </div>
            )}
          </div>
          {searched && !searching && numbersCard}
        </QuizQuestion>
      );

    case "location":
      return (
        <QuizQuestion
          {...q}
          title="Where is the property, and what are the key numbers?"
          hint="Enter the full street address, then fill in the details."
          onBack={goBack}
          onContinue={() => {
            if (!form.headline.trim()) set("headline", form.address.split(",")[0].trim());
            goNext();
          }}
          continueDisabled={!form.address.trim() || !numbersValid}
          size="md"
        >
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <FieldLabel required>Property address</FieldLabel>
            <AddressAutocomplete
              id="quiz-address"
              value={form.address}
              onChange={(v) => set("address", v)}
              placeholder="123 Main St, Orlando, FL 32801"
              showLabel={false}
              className="h-12 text-base"
            />
          </div>
          {form.address.trim().length >= 5 && numbersCard}
        </QuizQuestion>
      );

    case "details": {
      const visible = listTypes.filter((l) => l.list_name !== "Open House");
      return (
        <QuizQuestion
          {...q}
          title="A few more details"
          hint="Tag your listing, choose the property type, mark any open house and add key features."
          onBack={goBack}
          onContinue={goNext}
          continueDisabled={!hasTag || !form.propertyType || features.length < 1}
          size="md"
        >
          {/* Listing tag */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-base font-semibold text-slate-900">
              Listing tag <span className="text-red-500">*</span>
            </p>
            <p className="mb-4 text-sm text-slate-500">Appears on your flyer, e.g. “Just Listed”.</p>
            <div className="flex flex-wrap gap-2">
              {visible.map((l) => {
                const selected = form.listType.toLowerCase() === l.list_name.toLowerCase();
                return (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => set("listType", l.list_name)}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium transition",
                      selected
                        ? "border-blue-600 bg-blue-600 text-white shadow"
                        : "border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50"
                    )}
                  >
                    {selected ? <Check className="h-4 w-4" /> : <Tag className="h-4 w-4 text-slate-400" />}
                    {l.list_name}
                  </button>
                );
              })}
            </div>
            <div className="mt-5 border-t border-slate-100 pt-4">
              <FieldLabel hint={`max ${MAX_LISTING_TAG_LENGTH} characters`}>Or create your own tag</FieldLabel>
              <div className="flex gap-2">
                <Input
                  value={customTag}
                  maxLength={MAX_LISTING_TAG_LENGTH}
                  placeholder="Custom tag"
                  onChange={(e) => setCustomTag(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && saveCustomTag()}
                  className="h-11"
                />
                <Button onClick={saveCustomTag} disabled={savingTag || !customTag.trim()} className="h-11">
                  {savingTag ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />}
                  Save tag
                </Button>
              </div>
              {form.listType && (
                <p className="mt-3 text-sm text-slate-600">
                  Selected: <span className="font-semibold text-blue-700">{form.listType}</span>
                  {!hasTag && <span className="ml-2 text-amber-600">(save it to use it)</span>}
                </p>
              )}
            </div>
          </section>

          {/* Property type */}
          <section className="mt-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <p className="mb-3 text-base font-semibold text-slate-900">
              Property type <span className="text-red-500">*</span>
            </p>
            <div className="space-y-3">
              <OptionCard
                layout="row"
                icon={Home}
                tone="blue"
                title="Residential"
                description="Houses, condos, townhomes"
                selected={form.propertyType === "Residential"}
                onClick={() => set("propertyType", "Residential")}
              />
              <OptionCard
                layout="row"
                icon={Building2}
                tone="violet"
                title="Commercial"
                description="Offices, retail, mixed-use"
                selected={form.propertyType === "commercial"}
                onClick={() => set("propertyType", "commercial")}
              />
            </div>
          </section>

          {/* Open house */}
          <section className="mt-5 flex items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex min-w-0 items-center gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-sm">
                <CalendarCheck className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <p className="text-base font-semibold text-slate-900">Open house</p>
                <p className="text-sm text-slate-500">
                  {form.is_open_house ? "Marked as an open house — we'll highlight it." : "Turn on to highlight an open house."}
                </p>
              </div>
            </div>
            <Switch
              checked={form.is_open_house}
              onCheckedChange={(c) => set("is_open_house", Boolean(c))}
              aria-label="Open house"
            />
          </section>

          {/* Key features */}
          <section className="mt-5">
            <p className="mb-1 px-1 text-base font-semibold text-slate-900">
              Key features <span className="text-red-500">*</span>
            </p>
            <p className="mb-3 px-1 text-sm text-slate-500">Add at least one (up to 10).</p>
            <FeatureEditor features={features} onChange={setFeatures} suggestions={FEATURE_SUGGESTIONS} />
          </section>
        </QuizQuestion>
      );
    }

    case "photos":
      return (
        <QuizQuestion
          {...q}
          title="Add photos of the property"
          hint={`Upload up to ${MAX_PHOTOS} photos. Your first photo is the main image.`}
          onBack={goBack}
          onContinue={goNext}
          continueDisabled={photos.length < 1}
          summary={`${photos.length} of ${MAX_PHOTOS} photos added`}
          size="lg"
        >
          <PhotoPicker photos={photos} max={MAX_PHOTOS} onChange={setPhotos} onEnhanced={onEnhancedImageUpdate} />
        </QuizQuestion>
      );

    case "describe":
      return (
        <QuizQuestion
          {...q}
          title="Title and description"
          hint="Give your property a headline, then describe it — write your own or let AI help."
          onBack={goBack}
          onContinue={goNext}
          continueDisabled={!form.headline.trim() || !form.description.trim()}
          size="md"
        >
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <FieldLabel required>Property title</FieldLabel>
            <Input
              autoFocus
              value={form.headline}
              maxLength={255}
              placeholder="Charming 3-bedroom family home"
              onChange={(e) => set("headline", e.target.value)}
              className="h-12 text-base"
            />

            <div className="mt-6">
              <FieldLabel required>Description</FieldLabel>
              <Textarea
                rows={7}
                value={form.description}
                maxLength={MAX_DESCRIPTION_LENGTH}
                placeholder="Tell buyers what makes this property worth a visit…"
                onChange={(e) => set("description", e.target.value)}
                className="text-base"
              />
              <div className="mt-1 text-right text-xs text-slate-400">
                {form.description.length}/{MAX_DESCRIPTION_LENGTH}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <AiButton
                  label={descGen === 0 ? "Generate with AI" : descGen < MAX_GENERATION_ATTEMPTS ? "Regenerate" : "Limit reached"}
                  onClick={generateDescription}
                  loading={aiBusy === "gen-desc"}
                  disabled={descGen >= MAX_GENERATION_ATTEMPTS || !form.address}
                />
                <AiButton label="Improve" tone="slate" onClick={() => polish("description", "improve")} loading={aiBusy === "improve-description"} disabled={!form.description.trim()} />
                <AiButton label="Fix grammar" tone="slate" onClick={() => polish("description", "grammar")} loading={aiBusy === "grammar-description"} disabled={!form.description.trim()} />
              </div>
            </div>
          </div>
        </QuizQuestion>
      );

    case "subject":
      return (
        <QuizQuestion
          {...q}
          title="Write the email subject line"
          hint="This is what recipients see in their inbox."
          onBack={goBack}
          onContinue={goNext}
          continueDisabled={!form.subject.trim()}
          size="md"
        >
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <FieldLabel required>Email subject</FieldLabel>
            <Input
              value={form.subject}
              placeholder="Just listed: your dream home awaits"
              onChange={(e) => set("subject", e.target.value)}
              className="h-12 text-base"
            />
            <div className="mt-3 flex flex-wrap gap-2">
              <AiButton
                label={subjGen === 0 ? "Generate with AI" : subjGen < MAX_GENERATION_ATTEMPTS ? "Regenerate" : "Limit reached"}
                onClick={generateSubject}
                loading={aiBusy === "gen-subj"}
                disabled={subjGen >= MAX_GENERATION_ATTEMPTS || !form.address}
              />
              <AiButton label="Improve" tone="slate" onClick={() => polish("subject", "improve")} loading={aiBusy === "improve-subject"} disabled={!form.subject.trim()} />
              <AiButton label="Fix grammar" tone="slate" onClick={() => polish("subject", "grammar")} loading={aiBusy === "grammar-subject"} disabled={!form.subject.trim()} />
            </div>
          </div>
        </QuizQuestion>
      );

    case "review": {
      const rows: { label: string; value: React.ReactNode; to: Screen }[] = [
        { label: "Title", value: form.headline, to: "describe" },
        { label: "Address", value: form.address, to: source === "search" ? "address" : "location" },
        { label: "Tag", value: form.listType, to: "details" },
        { label: "Open house", value: form.is_open_house ? "Yes" : "No", to: "details" },
        { label: "Type", value: form.propertyType === "commercial" ? "Commercial" : "Residential", to: "details" },
        {
          label: "Details",
          value: `${money(form.price)} · ${form.bedrooms} bed · ${form.bathrooms} bath${form.sqft ? ` · ${form.sqft} sqft` : ""}`,
          to: source === "search" ? "address" : "location",
        },
        { label: "Subject", value: form.subject, to: "subject" },
      ];
      return (
        <QuizQuestion
          {...q}
          title="Everything look right?"
          hint="Review your property, then we'll save it and move on."
          onBack={goBack}
          onContinue={createProperty}
          continueLoading={saving}
          continueLabel="Save & use this property"
          size="lg"
        >
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            {photos[0] && (
              <div className="grid grid-cols-4 gap-1">
                <img src={photos[0].url} alt="" className="col-span-4 h-52 w-full object-cover sm:col-span-2 sm:row-span-2 sm:h-full" />
                {photos.slice(1).map((p) => (
                  <img key={p.id} src={p.url} alt="" className="h-24 w-full object-cover" />
                ))}
              </div>
            )}
            <dl className="divide-y divide-slate-100">
              {rows.map((r) => (
                <div key={r.label} className="flex items-start justify-between gap-4 px-5 py-3">
                  <div className="min-w-0">
                    <dt className="text-xs font-medium uppercase tracking-wide text-slate-400">{r.label}</dt>
                    <dd className="break-words text-sm font-medium text-slate-900">{r.value || "—"}</dd>
                  </div>
                  <button type="button" onClick={() => editFromReview(r.to)} className="shrink-0 text-sm font-medium text-blue-600 hover:underline">
                    Edit
                  </button>
                </div>
              ))}
              <div className="flex items-start justify-between gap-4 px-5 py-3">
                <div className="min-w-0">
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-400">Features</dt>
                  <dd className="mt-1 flex flex-wrap gap-1.5">
                    {features.map((f) => (
                      <span key={f} className="rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                        {f}
                      </span>
                    ))}
                  </dd>
                </div>
                <button type="button" onClick={() => editFromReview("details")} className="shrink-0 text-sm font-medium text-blue-600 hover:underline">
                  Edit
                </button>
              </div>
              <div className="flex items-start justify-between gap-4 px-5 py-3">
                <div className="min-w-0">
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-400">Description</dt>
                  <dd className="whitespace-pre-line text-sm text-slate-700">{form.description}</dd>
                </div>
                <button type="button" onClick={() => editFromReview("describe")} className="shrink-0 text-sm font-medium text-blue-600 hover:underline">
                  Edit
                </button>
              </div>
            </dl>
          </div>
        </QuizQuestion>
      );
    }

    default:
      return null;
  }
};
